import { supabase } from '../lib/supabase';
import { getMonthDateRange, toLocalDateString } from '../utils/dateUtils';

/**
 * @typedef {Object} Entry
 * @property {string} _id
 * @property {string} category
 * @property {number} amount
 * @property {string} description
 * @property {string|null} date
 *   YYYY-MM-DD. The date the cash moved. Always set for an expense; null for an
 *   income entry that has been earned but not yet paid.
 * @property {string} [earnedDate]
 *   YYYY-MM-DD. Income only — when the income was earned, which for on-the-spot
 *   payment is the same day as `date`.
 */

const requireUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  return user;
};

/**
 * CRUD for a table shaped like `expenses`.
 *
 * Income and expenses differ in copy, colour and one extra column, not in
 * shape, so both are served from one implementation rather than two
 * near-identical copies that would drift.
 *
 * @param {Object} config
 * @param {'expenses'|'income'} config.table
 *   A hardcoded literal at both call sites, never user input.
 * @param {Array<[string, string]>} [config.extraColumns]
 *   [snake_case_column, camelCaseField] pairs, so income carries earned_date
 *   through the same row mapper without a second copy of it.
 */
const createEntriesApi = ({ table, extraColumns = [] }) => {
  /** Supabase rows use `id`; the app uses `_id` everywhere. */
  const toEntry = (row) => {
    const entry = {
      _id: row.id,
      category: row.category,
      amount: row.amount,
      description: row.description,
      date: row.date,
    };
    extraColumns.forEach(([column, field]) => {
      entry[field] = row[column];
    });
    return entry;
  };

  /** The writable half of an entry, in database column names. */
  const toRow = (entry) => {
    const row = {
      category: entry.category,
      amount: Number(entry.amount),
      description: entry.description,
      date: entry.date,
    };
    extraColumns.forEach(([column, field]) => {
      row[column] = entry[field];
    });
    return row;
  };

  return {
    /**
     * One calendar month of entries, newest first.
     *
     * Filtering on `date` is what makes outstanding income invisible to every
     * budgeting total for free: a range predicate never matches NULL.
     *
     * @returns {Promise<Entry[]>}
     */
    list: async (month, year) => {
      const user = await requireUser();
      const { startDate, endDate } = getMonthDateRange(month, year);

      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: false });

      if (error) throw error;
      return data.map(toEntry);
    },

    /** @returns {Promise<Entry>} */
    create: async (entry) => {
      const user = await requireUser();
      const { data, error } = await supabase
        .from(table)
        .insert({ user_id: user.id, ...toRow(entry) })
        .select()
        .single();

      if (error) throw error;
      return toEntry(data);
    },

    /** @returns {Promise<Entry>} */
    update: async (entry) => {
      const user = await requireUser();
      const { data, error } = await supabase
        .from(table)
        .update(toRow(entry))
        // RLS already scopes this to the owner. The redundant user_id filter
        // makes the intent explicit and turns a mismatch into a clean "no rows"
        // error rather than a policy rejection.
        .eq('id', entry._id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      return toEntry(data);
    },

    /** @returns {Promise<string>} the deleted id */
    remove: async (id) => {
      const user = await requireUser();
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      return id;
    },

    toEntry,
  };
};

const expensesApi = createEntriesApi({ table: 'expenses' });
const incomeApi = createEntriesApi({
  table: 'income',
  extraColumns: [['earned_date', 'earnedDate']],
});

export const getExpenses = (month, year) => expensesApi.list(month, year);
export const postExpense = (entry) => expensesApi.create(entry);
export const updateExpense = (entry) => expensesApi.update(entry);
export const deleteExpense = (id) => expensesApi.remove(id);

export const getIncome = (month, year) => incomeApi.list(month, year);
export const postIncome = (entry) => incomeApi.create(entry);
export const updateIncome = (entry) => incomeApi.update(entry);
export const deleteIncome = (id) => incomeApi.remove(id);

/**
 * Income that has been earned but not yet paid.
 *
 * Deliberately not month-scoped: an invoice raised in March is still
 * outstanding in July, so scoping it to a month would hide exactly the rows
 * the user is looking for.
 *
 * @returns {Promise<Entry[]>} oldest debts last
 */
export const getOutstandingIncome = async () => {
  const user = await requireUser();

  const { data, error } = await supabase
    .from('income')
    .select('*')
    .eq('user_id', user.id)
    .is('date', null)
    .order('earned_date', { ascending: false });

  if (error) throw error;
  return data.map(incomeApi.toEntry);
};

/**
 * Stamps today as the cash-intake date, moving an entry out of Outstanding and
 * into the current month.
 * @returns {Promise<Entry>}
 */
export const markIncomePaid = (entry) =>
  updateIncome({ ...entry, date: toLocalDateString() });

/**
 * @typedef {Object} Account
 * @property {string[]} categories Expense categories.
 * @property {{ monthlyTotal: number }} budget
 * @property {string[]} incomeCategories
 * @property {number} incomeGoal
 */

/** @returns {Account} */
const toAccount = (row) => ({
  categories: row.categories ?? [],
  // Kept nested for compatibility with existing callers. The newer income
  // fields below are deliberately flat — don't propagate this shape.
  budget: { monthlyTotal: row.monthly_budget },
  // The `??` matters: against a database that hasn't had migration 002 applied
  // these columns come back undefined, and the app should degrade to "no
  // sources, no goal" rather than throw on `incomeCategories.map`.
  incomeCategories: row.income_categories ?? [],
  incomeGoal: row.monthly_income_goal ?? 0,
});

export const getAccount = async () => {
  const user = await requireUser();

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', user.id)
    .single();

  if (error && error.code === 'PGRST116') {
    // No profile row yet. Note this races the handle_new_user() trigger, so it
    // upserts rather than inserts: if the trigger lands between the select
    // above and the write below, an insert would raise a unique violation and
    // fail the whole account load.
    //
    // The income defaults are deliberately absent here — the schema owns them,
    // and `.select()` returns the filled row, so toAccount picks them up.
    // Duplicating them in JS would give the defaults two sources of truth.
    const defaultProfile = {
      id: user.id,
      categories: ['Rent', 'Groceries', 'Spending'],
      monthly_budget: 1200,
    };
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .upsert(defaultProfile)
      .select()
      .single();

    if (insertError) throw insertError;
    return toAccount(newProfile);
  }

  if (error) throw error;

  return toAccount(data);
};

/**
 * Writes only the fields present in `changes`; omitted fields are left alone.
 * PostgREST's upsert generates ON CONFLICT DO UPDATE SET over just the columns
 * in the payload, and on the insert path the omitted ones take their defaults.
 *
 * This partiality is load-bearing, not stylistic: Settings has four
 * independently-saved fields across three panels, and a full write from the
 * Income panel would blank the user's expense categories and budget.
 *
 * @param {{ budget?: number, categories?: string[],
 *           incomeCategories?: string[], incomeGoal?: number }} changes
 */
export const upsertAccount = async ({
  budget,
  categories,
  incomeCategories,
  incomeGoal,
} = {}) => {
  const user = await requireUser();

  const payload = { id: user.id };
  // Loose `!= null` so undefined and a not-yet-loaded null both mean "don't
  // touch". That also closes a latent bug: Settings passes `budget` straight
  // from props, which is null until the profile loads, and Number(null) === 0
  // would silently zero the user's budget.
  if (categories != null) payload.categories = categories;
  if (budget != null) payload.monthly_budget = Number(budget);
  if (incomeCategories != null) payload.income_categories = incomeCategories;
  if (incomeGoal != null) payload.monthly_income_goal = Number(incomeGoal);

  const { data, error } = await supabase
    .from('profiles')
    .upsert(payload)
    .select()
    .single();

  if (error) throw error;

  return { message: 'Account upserted successfully', newAccount: toAccount(data) };
};
